import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import { Users, Plus, Trash2, User, Phone, Mail, MapPin } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerManagementModal = ({ open, onClose, onSuccess }) => {
  const { isAdmin } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    address: '',
    phone: '',
    email: ''
  });

  useEffect(() => {
    if (open) {
      fetchCustomers();
    }
  }, [open]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      toast.error('Kunne ikke hente kunder');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!newCustomer.name) {
      toast.error('Navn er påkrævet');
      return;
    }

    setIsCreating(true);
    try {
      await axios.post(`${API}/customers`, newCustomer);
      toast.success('Kunde oprettet!');
      setNewCustomer({ name: '', address: '', phone: '', email: '' });
      setShowCreate(false);
      fetchCustomers();
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke oprette kunde');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (customerId, customerName) => {
    if (!window.confirm(`Er du sikker på du vil slette kunden "${customerName}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/customers/${customerId}`);
      toast.success('Kunde slettet');
      fetchCustomers();
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke slette kunde');
    }
  };

  const filteredCustomers = customers.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.phone.includes(searchQuery)
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="customer-management-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Kundeadministration
          </DialogTitle>
          <DialogDescription>
            Opret og administrer kunder
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search */}
          <Input
            placeholder="Søg kunde..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="customer-search"
          />

          {/* Create New Customer */}
          {!showCreate ? (
            <Button
              onClick={() => setShowCreate(true)}
              className="w-full"
              data-testid="show-create-customer-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Opret Ny Kunde
            </Button>
          ) : (
            <form onSubmit={handleCreate} className="space-y-3 p-4 border rounded-lg bg-muted/50">
              <h4 className="font-medium flex items-center gap-2">
                <User className="h-4 w-4" />
                Ny Kunde
              </h4>
              <div className="grid gap-3">
                <div className="space-y-1">
                  <Label>Navn *</Label>
                  <Input
                    placeholder="Kundens fulde navn"
                    value={newCustomer.name}
                    onChange={(e) => setNewCustomer({...newCustomer, name: e.target.value})}
                    data-testid="new-customer-name-input"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Adresse</Label>
                  <Input
                    placeholder="Adresse"
                    value={newCustomer.address}
                    onChange={(e) => setNewCustomer({...newCustomer, address: e.target.value})}
                    data-testid="new-customer-address-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label>Telefon</Label>
                    <Input
                      placeholder="Telefon"
                      value={newCustomer.phone}
                      onChange={(e) => setNewCustomer({...newCustomer, phone: e.target.value})}
                      data-testid="new-customer-phone-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      placeholder="Email"
                      value={newCustomer.email}
                      onChange={(e) => setNewCustomer({...newCustomer, email: e.target.value})}
                      data-testid="new-customer-email-input"
                    />
                  </div>
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreate(false)}
                  className="flex-1"
                >
                  Annuller
                </Button>
                <Button
                  type="submit"
                  disabled={isCreating}
                  className="flex-1"
                  data-testid="create-customer-btn"
                >
                  {isCreating ? 'Opretter...' : 'Opret'}
                </Button>
              </div>
            </form>
          )}

          <Separator />

          {/* Customer List */}
          <div className="space-y-2">
            <h4 className="font-medium">Kunder ({filteredCustomers.length})</h4>
            {loading ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
              </div>
            ) : filteredCustomers.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                Ingen kunder fundet
              </p>
            ) : (
              <ScrollArea className="h-[300px]">
                <div className="space-y-2 pr-4">
                  {filteredCustomers.map((customer) => (
                    <div
                      key={customer.id}
                      className="p-3 border rounded-lg space-y-2"
                      data-testid={`customer-item-${customer.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <h5 className="font-medium">{customer.name}</h5>
                        {isAdmin() && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(customer.id, customer.name)}
                            className="text-destructive hover:text-destructive h-8 w-8"
                            data-testid={`delete-customer-${customer.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground space-y-1">
                        {customer.phone && (
                          <p className="flex items-center gap-2">
                            <Phone className="h-3 w-3" />
                            {customer.phone}
                          </p>
                        )}
                        {customer.email && (
                          <p className="flex items-center gap-2">
                            <Mail className="h-3 w-3" />
                            {customer.email}
                          </p>
                        )}
                        {customer.address && (
                          <p className="flex items-center gap-2">
                            <MapPin className="h-3 w-3" />
                            {customer.address}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerManagementModal;
